// pageRegistry.ts — page definitions (PAGES), section component map (COMPONENTS),
// and HasData keys for auto-hide. Per-route React modules live in `templatePages/`
// (see `templatePages/pageBodyRegistry.tsx` + `TemplatePage.tsx`).

import { lazy } from "react";
import type { PageDef, SectionOptions, SectionCfg, SectionSpec, Tweaks } from "./types";
import type { HasData } from "./useCreatorDataFromDB";
import type { ThemeTokens } from "./types";
import {
  BioSection,
  HighlightsSection,
  BannerSection,
  HighlightCardsSection,
  LinksSection,
  CarouselSection,
  FeaturedHighlight,
  PressSection,
  NewsletterSection,
  ProductGrid,
  ContentGrid,
  PlatformHighlightGrid,
  ShopFilters,
  PageHeader,
  JournalGrid,
  PostHero,
  PostBody,
  PostAuthor,
  AboutStory,
  MoreFromSection,
  ContactForm,
  RecentTimeline,
  SearchHeader,
  TextBlock,
} from "./TemplateSections";
import { RelatedContentSection } from "./RelatedContentSection";
import { DEFAULT_JOURNAL_GRID_HEADING } from "./websiteInspectorFieldLabels";
import { SchemaBlockSection } from "./SchemaBlockSection";
import {
  FeaturedCategoriesSection,
  FeaturedCollectionsSection,
} from "./FeaturedBand/FeaturedBandSections";
import {
  FeaturedOutfitSection,
  RecentLooksSection,
} from "./ContentLooksSections";
import {
  blankCustomPageDef,
  extraSectionsOf,
  findCustomWebsitePage,
  isCustomWebsitePageKey,
  isFreeformAddPage,
  mergePageDefWithExtra,
} from "./customWebsitePages";
import { isContentLooksCatalogType } from "./shopPageMerch";

/** LiB feed pulls StayUpToDate — keep off the public home JS critical path. */
const LinkInBioStackSection = lazy(() =>
  import("./LinkInBioStackSection").then((m) => ({
    default: m.LinkInBioStackSection,
  })),
);
const LinkInBioLiveSection = lazy(() =>
  import("./WebsiteTemplateLinkInBioFeed").then((m) => ({
    default: m.LinkInBioLiveSection,
  })),
);
const StoreProductSection = lazy(() =>
  import("./StoreProductSection").then((m) => ({
    default: m.StoreProductSection,
  })),
);

// ── Component type ─────────────────────────────────────────────
export type SectionComponent = React.ComponentType<{
  T: ThemeTokens;
  cfg?: SectionCfg;
  [key: string]: unknown;
}> & { options?: SectionOptions };

/**
 * Merges persisted `pages[pageKey].order` with the canonical `PAGES[pageKey].sections`
 * so newly added registry sections appear without wiping custom order, and stale IDs
 * for sections that have been removed from the registry are silently discarded.
 */
export function resolvePageSectionOrder(
  savedOrder: string[] | undefined,
  def: PageDef | undefined,
): string[] {
  const defaultOrder = (def?.sections ?? []).map((s) => s.id);
  if (!savedOrder?.length) return defaultOrder;
  // Filter out stale IDs that no longer exist in the current registry.
  const validIds = new Set(defaultOrder);
  const filteredOrder = savedOrder.filter((id) => validIds.has(id));
  if (!filteredOrder.length) return defaultOrder;
  const seen = new Set(filteredOrder);
  const missing = defaultOrder.filter((id) => !seen.has(id));
  if (!missing.length) return filteredOrder;

  // Insert each new registry section after its nearest prior neighbor from the
  // canonical order (so "related" lands after "body", not after newsletter).
  const next = [...filteredOrder];
  for (const id of missing) {
    const defIdx = defaultOrder.indexOf(id);
    let insertAt = next.length;
    for (let i = defIdx - 1; i >= 0; i--) {
      const prior = defaultOrder[i]!;
      const at = next.indexOf(prior);
      if (at >= 0) {
        insertAt = at + 1;
        break;
      }
    }
    if (insertAt === next.length) {
      for (let i = defIdx + 1; i < defaultOrder.length; i++) {
        const after = defaultOrder[i]!;
        const at = next.indexOf(after);
        if (at >= 0) {
          insertAt = at;
          break;
        }
      }
    }
    next.splice(insertAt, 0, id);
  }
  return next;
}

export const HOME_HERO_SECTION_ID = "bio";

/**
 * Default Home sections that must not render.
 * The canvas keeps the Hero and Shop This Past Week (`carouselProducts`).
 * Creator-added extras (`x_…`) are unchanged.
 */
const HOME_HIDDEN_DEFAULT_SECTION_IDS = new Set([
  "carouselShop",
  "heroBanner",
  "highlightCards",
  "promoBanner",
]);

export function isHomeDefaultSectionVisible(sectionId: string): boolean {
  if (sectionId.startsWith("x_")) return true;
  return !HOME_HIDDEN_DEFAULT_SECTION_IDS.has(sectionId);
}

export function isRibbonCatalogType(catalogType: string): boolean {
  return catalogType === "ribbon";
}

export function isHomeHeroSpec(spec: SectionSpec | undefined): boolean {
  return spec?.id === HOME_HERO_SECTION_ID || spec?.type === "Bio";
}

export function isHomeRibbonSpec(
  spec: SectionSpec | undefined,
  cfg?: SectionCfg,
): boolean {
  const t = String(
    spec?.catalogType || cfg?.catalogType || cfg?.blockType || "",
  );
  return t === "ribbon";
}

/** Home: only Ribbon Alert may sit above Bio. Everything else stays below. */
export function clampHomeHeroOrder(
  order: string[],
  def: PageDef | undefined,
  pageCfg?: Record<string, SectionCfg>,
): string[] {
  if (!def) return order;
  const heroId = def.sections.find((s) => isHomeHeroSpec(s))?.id;
  if (!heroId || !order.includes(heroId)) return order;
  const byId = new Map(def.sections.map((s) => [s.id, s]));
  const leadingRibbons: string[] = [];
  const rest: string[] = [];
  let seenHero = false;
  for (const id of order) {
    if (id === heroId) {
      seenHero = true;
      continue;
    }
    if (!seenHero && isHomeRibbonSpec(byId.get(id), pageCfg?.[id])) {
      leadingRibbons.push(id);
    } else {
      rest.push(id);
    }
  }
  return [...leadingRibbons, heroId, ...rest];
}

export function homeHeroVisibleIndex(
  visibleIds: string[],
  def: PageDef | undefined,
): number {
  if (!def) return -1;
  return visibleIds.findIndex((id) =>
    isHomeHeroSpec(def.sections.find((s) => s.id === id)),
  );
}

/**
 * Section ids the website editor actually shows (insert-line indices).
 * Must match `OrderedSectionsRenderer` filters so Add-between lands at the
 * same spot as the ghost insert (not above the section when the click was below).
 */
export function listEditorVisiblePageSectionIds(args: {
  pageKey: string;
  fullOrder: string[];
  def: PageDef | undefined;
  hidden: Record<string, boolean | undefined>;
  /** When Home has banners, HighlightCards is opt-in (same as the renderer). */
  hasBanners?: boolean;
  /** Page cfg — used to keep unbound legacy banner slots out when extras own banners. */
  cfg?: Record<string, Record<string, unknown> | undefined>;
  /** True when this page already has Banner extras (new-slot model). */
  hasBannerExtras?: boolean;
  /** Email Campaigns flag is on and at least one template campaign exists. */
  showNewsletter?: boolean;
}): string[] {
  const {
    pageKey,
    fullOrder,
    def,
    hidden,
    hasBanners,
    cfg,
    hasBannerExtras,
  } = args;
  return fullOrder.filter((id) => {
    const s = def?.sections.find((x) => x.id === id);
    if (!s) return false;
    if (s.type === "Newsletter") {
      const bound = String(cfg?.[id]?.componentId || "").trim();
      return Boolean(bound) && hidden[id] !== true;
    }
    if (s.defaultHidden) {
      if (hidden[id] !== false) return false;
    } else if (hidden[id] === true) {
      return false;
    }
    if (
      pageKey === "home" &&
      id === "highlightCards" &&
      hasBanners === true &&
      hidden.highlightCards !== false
    ) {
      return false;
    }
    if (
      hasBannerExtras &&
      (id === "heroBanner" ||
        id === "promoBanner" ||
        (pageKey === "contact" && id === "banner")) &&
      !String(cfg?.[id]?.componentId || "").trim()
    ) {
      return false;
    }
    if (
      pageKey === "home" &&
      !isHomeDefaultSectionVisible(id)
    ) {
      return false;
    }
    return true;
  });
}

/** True when this insert slot would place a new section above Home Bio. */
export function insertSlotIsAboveHomeHero(
  pageKey: string,
  insertAt: number,
  visibleIds: string[],
  def: PageDef | undefined,
): boolean {
  if (pageKey !== "home" || insertAt < 0) return false;
  const heroIdx = homeHeroVisibleIndex(visibleIds, def);
  if (heroIdx < 0) return false;
  return insertAt <= heroIdx;
}

export function clampHomeInsertAt(
  pageKey: string,
  insertAt: number,
  catalogType: string,
  visibleIds: string[],
  def: PageDef | undefined,
): number {
  // Link in Bio live feed owns the page body (bio + stack). Never insert page
  // extras above it — Lib chrome insert indices (0, 1, …) would otherwise land
  // at the top of the page.
  if (pageKey === "linkInBio") {
    if (insertAt < 0) return insertAt;
    const feedIdx = visibleIds.findIndex((id) => {
      if (id === "feed") return true;
      return def?.sections.find((s) => s.id === id)?.type === "LinkInBioLive";
    });
    if (feedIdx < 0) return insertAt;
    return Math.max(insertAt, feedIdx + 1);
  }
  if (pageKey !== "home" || insertAt < 0 || isRibbonCatalogType(catalogType)) {
    return insertAt;
  }
  const heroIdx = homeHeroVisibleIndex(visibleIds, def);
  if (heroIdx < 0) return insertAt;
  return Math.max(insertAt, heroIdx + 1);
}

/** Apply a drag reorder among visible sections back onto the full page order. */
export function mergeVisibleSectionReorder(
  fullOrder: string[],
  visibleIds: string[],
  oldIndex: number,
  newIndex: number,
): string[] {
  if (oldIndex === newIndex || oldIndex < 0 || newIndex < 0) return fullOrder;
  const reorderedVisible = [...visibleIds];
  const [moved] = reorderedVisible.splice(oldIndex, 1);
  reorderedVisible.splice(newIndex, 0, moved);
  const visibleSet = new Set(visibleIds);
  let vi = 0;
  return fullOrder.map((id) =>
    visibleSet.has(id) ? reorderedVisible[vi++]! : id,
  );
}

/**
 * Place `sectionId` so it appears at `insertAt` among the current visible sections
 * (same index the Add-component ghost uses). `insertAt < 0` appends at the end.
 */
export function insertSectionAtVisibleIndex(
  fullOrder: string[],
  visibleIds: string[],
  sectionId: string,
  insertAt: number,
): string[] {
  const visWithout = visibleIds.filter((id) => id !== sectionId);
  const at =
    insertAt < 0
      ? visWithout.length
      : Math.max(0, Math.min(insertAt, visWithout.length));
  const base = fullOrder.filter((id) => id !== sectionId);
  const remainingVis = new Set(visWithout);
  let spliceAt = base.length;
  if (at === 0) {
    const idx = base.findIndex((id) => remainingVis.has(id));
    spliceAt = idx < 0 ? 0 : idx;
  } else {
    let seen = 0;
    for (let i = 0; i < base.length; i++) {
      if (!remainingVis.has(base[i]!)) continue;
      seen += 1;
      if (seen === at) {
        spliceAt = i + 1;
        break;
      }
    }
  }
  const next = [...base];
  next.splice(spliceAt, 0, sectionId);
  return next;
}

/** Home (or page) section slot that a catalog add type wires into. */
export function catalogTypeToPageSectionId(
  catalogType: string,
  pageKey: string,
): string | null {
  if (
    catalogType === "featured-categories" &&
    (pageKey === "shop" || pageKey === "content")
  ) {
    return "featuredCategories";
  }
  if (
    catalogType === "featured-collections" &&
    (pageKey === "shop" || pageKey === "content")
  ) {
    return "featuredCollections";
  }

  const contentOrProduct =
    catalogType === "content-feed" ||
    catalogType === "trending-content" ||
    catalogType === "content-keyword" ||
    catalogType === "custom-content" ||
    catalogType === "product-feed" ||
    catalogType === "trending-products" ||
    catalogType === "product-keyword" ||
    catalogType === "custom-products";

  if (pageKey === "home") {
    const productFeed =
      catalogType === "product-feed" ||
      catalogType === "trending-products" ||
      catalogType === "product-keyword" ||
      catalogType === "custom-products";
    if (productFeed) return "carouselProducts";
    if (contentOrProduct) return "carouselShop";
    // Banners and highlights always become their own section. Reusing
    // heroBanner / highlightCards overwrote the previous row.
    if (catalogType === "banner" || catalogType === "highlight") return null;
    if (catalogType === "link-section") return "links";
    if (catalogType === "highlight") return "highlightCards";
    if (catalogType === "newsletter") return "newsletter";
    return null;
  }
  if (pageKey === "about") {
    if (contentOrProduct) return "carousel";
    if (catalogType === "newsletter") return "newsletter";
    return null;
  }
  if (pageKey === "contact") {
    if (contentOrProduct) return "carousel";
    if (catalogType === "link-section") return "links";
    if (catalogType === "banner") return null;
    if (catalogType === "newsletter") return "newsletter";
    return null;
  }
  // Shop / Recent Content: new feeds become tabs in the existing grid —
  // don't invent orphan section ids or falsely bind ProductGrid/ContentGrid.
  if (pageKey === "journal" && contentOrProduct) return "carousel";
  return null;
}

/** Whether this catalog type can be placed on the current page (or creates a real tab). */
export function catalogTypeAvailableOnPage(
  catalogType: string,
  pageKey: string,
): boolean {
  // Blog / About / Contact: Add component is disabled.
  if (
    pageKey === "about" ||
    pageKey === "contact" ||
    pageKey === "journal" ||
    pageKey === "journalPost" ||
    pageKey === "blog"
  ) {
    return false;
  }
  // Content Looks stay Content-page-only.
  if (isContentLooksCatalogType(catalogType)) {
    return pageKey === "content";
  }
  // Featured Categories / Collections on Products and Content only.
  if (
    catalogType === "featured-categories" ||
    catalogType === "featured-collections"
  ) {
    return pageKey === "shop" || pageKey === "content";
  }
  // Products page: Featured modules only (bands gated above; no Digital Product).
  if (pageKey === "shop") {
    return (
      catalogType === "featured-category" ||
      catalogType === "featured-brands" ||
      catalogType === "shop-brand"
    );
  }
  // Content page: Looks + Featured bands only (gated above).
  if (pageKey === "content") {
    return false;
  }
  // Link in Bio: Content / Products / Links & Features / Embeds / Campaigns —
  // no Button, Ribbon, or schema layout blocks.
  if (pageKey === "linkInBio") {
    const libAddTypes = new Set([
      "content-feed",
      "trending-content",
      "content-keyword",
      "custom-content",
      "this-week-content",
      "featured-content",
      "product-feed",
      "trending-products",
      "product-keyword",
      "custom-products",
      "store-products",
      "link-section",
      "highlight",
      "banner",
      "group",
      "embed",
      "embed-newsletter-kit",
      "flodesk",
      "fillout",
      "campaign",
    ]);
    return libAddTypes.has(catalogType);
  }
  // Stay-row embeds / forms / campaigns are LiB (and classic) feed types — not
  // website Home schema blocks.
  if (
    catalogType === "embed-newsletter-kit" ||
    catalogType === "flodesk" ||
    catalogType === "fillout"
  ) {
    return false;
  }
  if (catalogType === "group") {
    // Website Home Featured Carousel can resolve a carousel-collection row.
    return pageKey === "home" || isFreeformAddPage(pageKey);
  }
  if (isFreeformAddPage(pageKey)) return true;
  if (catalogTypeToPageSectionId(catalogType, pageKey)) return true;
  return false;
}

export function isTemplatePageKey(
  pageKey: string,
  tweaks?: Tweaks | null,
): boolean {
  if (pageKey in PAGES) return true;
  if (!isCustomWebsitePageKey(pageKey)) return false;
  if (!tweaks) return true;
  return Boolean(findCustomWebsitePage(tweaks, pageKey));
}

export function getPageDef(
  pageKey: string,
  tweaks?: Tweaks | null,
): PageDef | undefined {
  const custom = findCustomWebsitePage(tweaks, pageKey);
  const base = custom ? blankCustomPageDef(custom) : PAGES[pageKey];
  if (!base) return undefined;
  // Extras live under the stable page id, not the public slug key.
  const storageKey = custom?.id || pageKey;
  return mergePageDefWithExtra(base, extraSectionsOf(tweaks?.pages?.[storageKey]));
}

// ── Page registry ─────────────────────────────────────────────
// Each page is an ordered list of section specs.
// Users can reorder/hide sections via the Tweaks panel.
export const PAGES: Record<string, PageDef> = {
  // ── Home ────────────────────────────────────────────────────
  // Home canvas: Hero, then Shop This Past Week.
  // This Week's Content, highlight cards, and the banner slots stay in the
  // registry for saved orders but are not rendered.
  home: {
    label: "Home",
    sections: [
      // ① Bio (hero) — only a Ribbon Alert may sit above this
      { id: "bio", type: "Bio", label: "Bio" },

      // ② This Week's Content — static (peek) strip bound to the day-window tab
      {
        id: "carouselShop",
        type: "Carousel",
        label: "This Week's Content",
        props: { idx: 0 },
        defaults: {
          style: "peek",
          heading: "This Week's Content",
          carouselSlug: "auto",
          preferFeed: "this-week",
        },
      },

      // ③ Banner #1 (hero slot). When the creator has no banners but has
      //    highlights, HighlightCards fills this band (see OrderedSectionsRenderer).
      {
        id: "heroBanner",
        type: "Banner",
        label: "Banner",
        props: { which: "hero" },
      },

      // Highlight cards — shown by default when present and no banners exist
      {
        id: "highlightCards",
        type: "HighlightCards",
        label: "Highlights",
      },

      // Shop This Past Week — last 7 days of products
      {
        id: "carouselProducts",
        type: "Carousel",
        label: "Shop This Past Week",
        props: { idx: 1 },
        defaults: {
          style: "marquee",
          heading: "Shop This Past Week",
          carouselSlug: "auto",
          preferFeed: "shop-this-past-week",
        },
      },

      // ⑤ Banner #2 (promo slot)
      {
        id: "promoBanner",
        type: "Banner",
        label: "Banner",
        props: { which: "promo" },
        defaults: { style: "split" },
      },

      // Opt-in — not part of the existing-customer default stack
      {
        id: "links",
        type: "Links",
        label: "Quick Links",
        defaultHidden: true,
      },

      // Newsletter temporarily removed from the default home (also hard-gated
      // in OrderedSectionsRenderer).
      {
        id: "newsletter",
        type: "Newsletter",
        label: "Newsletter",
        defaultHidden: true,
      },
    ],
  },

  // ── Shop ────────────────────────────────────────────────────
  // Products page: no page title (categories / inspiration own the fold).
  // Header stays in the registry so older saved orders don't break; it is
  // filtered out at render time in OrderedSectionsRenderer.
  shop: {
    label: "Products",
    sections: [
      {
        id: "header",
        type: "PageHeader",
        label: "Header",
        props: { heading: "Products", lead: "" },
        defaultHidden: true,
      },
      {
        id: "featuredCategories",
        type: "FeaturedCategories",
        label: "Featured categories",
        defaultHidden: true,
        defaults: {
          count: Number("2"),
          imageMode: "single",
        },
      },
      {
        id: "featuredCollections",
        type: "FeaturedCollections",
        label: "Featured collections",
        defaultHidden: true,
        defaults: {
          count: Number("2"),
          imageMode: "single",
        },
      },
      { id: "grid", type: "ProductGrid", label: "Product grid" },
    ],
  },

  // ── Blog (Journal) ──────────────────────────────────────────
  journal: {
    label: "Blog",
    sections: [
      {
        id: "header",
        type: "PageHeader",
        label: "Header",
        props: {
          heading: "Blog",
          lead: "A weekly notebook on style, rooms, and the soft middle ground between them.",
        },
      },
      {
        id: "grid",
        type: "JournalGrid",
        label: "All posts",
        defaults: {
          style: "list",
          count: 24,
          heading: DEFAULT_JOURNAL_GRID_HEADING,
        },
      },
      {
        id: "carousel",
        type: "Carousel",
        label: "Carousel",
        defaultHidden: true,
        props: { idx: 0 },
      },
    ],
  },

  // ── Blog Post ────────────────────────────────────────────────
  journalPost: {
    label: "Blog article",
    sections: [
      {
        id: "hero",
        type: "PostHero",
        label: "Post hero",
        defaults: { style: "split", showDate: true, showMeta: true, showExcerpt: true },
      },
      {
        id: "author",
        type: "PostAuthor",
        label: "Author",
        defaultHidden: true,
        defaults: { style: "inline", showBio: true, showCta: false },
      },
      {
        id: "body",
        type: "PostBody",
        label: "Post body",
        defaults: { showMorePosts: true, bodyLayout: "editorial" },
      },
      {
        id: "related",
        type: "RelatedContent",
        label: "Related content",
        defaultHidden: true,
        defaults: {
          style: "carousel",
          heading: "Related content",
          eyebrow: "From this story",
          productsHeading: "Related products",
          productsEyebrow: "Shop the look",
        },
      },
      { id: "newsletter", type: "Newsletter", label: "Newsletter", defaultHidden: true },
    ],
  },

  // ── About ────────────────────────────────────────────────────
  about: {
    label: "About",
    sections: [
      {
        id: "header",
        type: "PageHeader",
        label: "Header",
        props: {
          heading: "About",
          lead: "Get to know me",
        },
      },
      { id: "story", type: "AboutStory", label: "Story" },

      // ── Optional sections (defaultHidden — enabled from the tweaks panel) ──
      {
        id: "highlights",
        type: "Highlights",
        label: "Story Highlights",
        defaultHidden: true,
      },
      {
        id: "textBlock",
        type: "TextBlock",
        label: "Text Block",
        defaultHidden: true,
      },
      {
        id: "carousel",
        type: "Carousel",
        label: "Carousel",
        defaultHidden: true,
        props: { idx: 0 },
      },
      {
        id: "newsletter",
        type: "Newsletter",
        label: "Newsletter",
        defaultHidden: true,
      },
    ],
  },

  // ── Press ────────────────────────────────────────────────────
  press: {
    label: "Press",
    sections: [
      {
        id: "header",
        type: "PageHeader",
        label: "Header",
        props: {
          heading: "Press & features",
          lead: "Selected mentions, interviews, and features.",
        },
      },
      {
        id: "list",
        type: "Press",
        label: "Press list",
        defaults: { count: 5 },
      },
      {
        id: "logos",
        type: "Press",
        label: "Outlet wall",
        defaults: { style: "logos" },
      },
      {
        id: "contact",
        type: "Banner",
        label: "For press enquiries",
        props: { which: "footer" },
        defaults: { style: "contained" },
      },
      {
        id: "carousel",
        type: "Carousel",
        label: "Carousel",
        defaultHidden: true,
        props: { idx: 0 },
      },
    ],
  },

  // ── Contact ──────────────────────────────────────────────────
  contact: {
    label: "Contact",
    sections: [
      {
        id: "header",
        type: "PageHeader",
        label: "Header",
        props: {
          heading: "Get in touch",
          lead: "For partnerships, press, editorial, and the occasional letter.",
        },
      },
      { id: "form", type: "ContactForm", label: "Form" },

      // ── Optional sections (defaultHidden — enabled from the tweaks panel) ──
      {
        id: "textBlock",
        type: "TextBlock",
        label: "Text Block",
        defaultHidden: true,
      },
      {
        id: "highlights",
        type: "Highlights",
        label: "Story Highlights",
        defaultHidden: true,
      },
      {
        id: "links",
        type: "Links",
        label: "Quick Links",
        defaultHidden: true,
      },
      {
        id: "banner",
        type: "Banner",
        label: "Feature Banner",
        defaultHidden: true,
        props: { which: "hero" },
      },
      {
        id: "carousel",
        type: "Carousel",
        label: "Carousel",
        defaultHidden: true,
        props: { idx: 0 },
      },
      {
        id: "newsletter",
        type: "Newsletter",
        label: "Newsletter",
        defaultHidden: true,
      },
    ],
  },

  // ── Newsletter ───────────────────────────────────────────────
  // newsletter: {
  //   label: "Newsletter",
  //   sections: [
  //     {
  //       id: "header",
  //       type: "PageHeader",
  //       label: "Header",
  //       props: {
  //         heading: "Soft Middle",
  //         lead: "Sunday letters on style, the small kind of beauty, and what I sent back this week.",
  //       },
  //     },
  //     {
  //       id: "form",
  //       type: "Newsletter",
  //       label: "Subscribe form",
  //       defaults: { style: "banner" },
  //     },
  //     {
  //       id: "recent",
  //       type: "JournalGrid",
  //       label: "Past issues",
  //       defaults: { count: 6 },
  //     },
  //   ],
  // },

  // ── Content (StayUpToDate feed) ───────────────────────────────
  // Content page: no page title (platforms / looks own the fold) — same as
  // Products. Header stays in the registry so older saved orders don't break;
  // it is filtered out at render time in OrderedSectionsRenderer.
  content: {
    label: "Content",
    sections: [
      {
        id: "header",
        type: "PageHeader",
        label: "Header",
        props: {
          heading: "Content",
          lead: "",
        },
        defaultHidden: true,
      },
      {
        id: "featuredCategories",
        type: "FeaturedCategories",
        label: "Featured categories",
        defaultHidden: true,
        defaults: {
          count: Number("2"),
          imageMode: "single",
        },
      },
      {
        id: "featuredCollections",
        type: "FeaturedCollections",
        label: "Featured collections",
        defaultHidden: true,
        defaults: {
          count: 2,
          imageMode: "single",
        },
      },
      { id: "grid",       type: "ContentGrid", label: "Content grid" },
      { id: "newsletter", type: "Newsletter",      label: "Newsletter" },
    ],
  },

  // ── Search ───────────────────────────────────────────────────
  search: {
    label: "Search",
    sections: [
      {
        id: "header",
        type: "SearchHeader",
        label: "Search bar",
        defaults: { placeholder: "Search" },
      },
    ],
  },

  // ── Link in Bio (`/:username` structure + template design) ──
  linkInBio: {
    label: "Link in Bio",
    sections: [
      { id: "feed", type: "LinkInBioLive", label: "Link in Bio" },
    ],
  },
};

/** Dashboard editor: omit these `PAGES` keys from the preview tab strip and page picker (see `BrowserChrome` + `PanelBody`). Public `/website/.../press` may still resolve. `platform` is never a site page (highlights open Content). */
export const EDITOR_NAV_HIDDEN_PAGE_KEYS: ReadonlySet<string> = new Set([
  "press",
  "platform",
]);

/**
 * Main nav + search: mount these page bodies in parallel (inactive
 * layers use the `hidden` attribute) so switching template tabs does not cold-mount.
 * `journalPost` is not listed — it is mounted only after the user opens a post.
 */
export const TEMPLATE_PRELOAD_PAGE_KEYS: readonly string[] = [
  "home",
  "shop",
  "journal",
  "about",
  "press",
  "contact",
  "newsletter",
  "content",
  "linkInBio",
  "search",
].filter((key) => key in PAGES);

// ── Component map ─────────────────────────────────────────────
// type key (from PAGES section specs) → React component
export const COMPONENTS: Record<string, SectionComponent> = {
  Bio:            BioSection,
  Highlights:     HighlightsSection,
  Banner:         BannerSection as SectionComponent,
  HighlightCards: HighlightCardsSection,
  Links:          LinksSection,
  Carousel:       CarouselSection as SectionComponent,
  FeaturedCategories: FeaturedCategoriesSection as SectionComponent,
  FeaturedCollections: FeaturedCollectionsSection as SectionComponent,
  FeaturedOutfit: FeaturedOutfitSection as SectionComponent,
  RecentLooks:    RecentLooksSection as SectionComponent,
  MoreFrom:       MoreFromSection,
  Featured:       FeaturedHighlight,
  Press:          PressSection,
  Newsletter:     NewsletterSection,
  PageHeader:     PageHeader as SectionComponent,
  ShopFilters:    ShopFilters,
  ProductGrid:    ProductGrid,
  ContentGrid:    ContentGrid,
  PlatformHighlightGrid: PlatformHighlightGrid,
  JournalGrid:    JournalGrid,
  PostHero:       PostHero,
  PostBody:       PostBody,
  PostAuthor:     PostAuthor,
  RelatedContent: RelatedContentSection as SectionComponent,
  AboutStory:     AboutStory,
  TextBlock:      TextBlock,
  ContactForm:    ContactForm,
  RecentTimeline: RecentTimeline,
  SearchHeader:   SearchHeader,
  LinkInBioStack: LinkInBioStackSection as SectionComponent,
  LinkInBioLive: LinkInBioLiveSection as SectionComponent,
  StoreProduct:   StoreProductSection as SectionComponent,
  SchemaBlock:    SchemaBlockSection as SectionComponent,
};

// ── Section → data-key map ────────────────────────────────────
// null   = no data requirement (always render)
// string = hasData[key] must be true for the section to auto-show in live mode
export const SECTION_DATA_KEY: Record<string, keyof HasData | null> = {
  Bio:            "bio",
  Highlights:     "highlights",
  Banner:         "banners",
  HighlightCards: "highlightCards",
  // Always mount. Gating on hasData.links unmounted the section after the
  // first paint (links still false) and mounted it again when link rows
  // arrived, so the block flashed in and out. LinksSection hides itself
  // once it knows the section is actually empty.
  Links:          null,
  Carousel:       "carousels",
  // Always mount — bands live inside ProductGrid and manage their own loaders.
  // Gating Categories on "products" and Collections on "carousels" made
  // Collections paint alone (on top) until products arrived, then Categories
  // inserted above and shoved Collections back to its real index.
  FeaturedCategories: null,
  FeaturedCollections: null,
  FeaturedOutfit: null,
  RecentLooks:    null,
  Featured:       "banners",
  Press:          "press",
  Newsletter:     "campaign",
  PageHeader:     null,
  ShopFilters:    "products",
  // Always mount — ProductGrid manages its own skeleton loader and empty state (same pattern as ContentGrid/JournalGrid).
  // Gating on "products" caused the section to be invisibly filtered out while hasData was still loading,
  // so a hard refresh to a filtered shop URL (/shop/primary/secondary/brand) showed a blank page with no skeleton.
  ProductGrid:    null,
  // Always mount on the content page; empty carousel tabs fall back to platform filters inside ContentGrid.
  ContentGrid:    null,
  PlatformHighlightGrid: null,
  // Always mount — component shows a public empty message when there are no posts.
  JournalGrid:    null,
  // Always mount — PostHero/PostBody manage their own skeletons / empty states
  // (same pattern as JournalGrid). Gating on "journal" hid the whole article
  // while hasData was still false, so deep links to /blog/:slug looked blank.
  PostHero:       null,
  PostBody:       null,
  PostAuthor:     null,
  RelatedContent: null,
  AboutStory:     "about",
  MoreFrom:       "carousels",
  TextBlock:      null,   // always renderable; shows empty-state hint in editor mode
  // Contact form is data-driven: hidden unless creator saved a `contact_page` row.
  ContactForm:    null,      // self-manages loading skeleton; shows null when disabled
  RecentTimeline: "recent",
  SearchHeader:   null,
  LinkInBioStack: "linkInBio",
  // Always mount — WebsiteTemplateLinkInBioFeed manages its own skeleton, loading, and
  // empty state internally (same pattern as ContentGrid/ProductGrid/JournalGrid).
  // Gating on "linkInBio" (stayuptodate_components count) caused the page body to be
  // empty for creators with no components, while the nav "Link in Bio" link still showed.
  LinkInBioLive: null,
  StoreProduct:   null,
  SchemaBlock:    null,
};
